<template>
  <div
    ref="carouselRef"
    class="glide space-y-4"
    role="region"
    aria-label="carousel"
    aria-live="polite"
    aria-atomic="true"
  >
    <div class="glide__track" data-glide-el="track">
      <div ref="trackRef" class="glide__slides" role="group">
        <slot />
      </div>
    </div>
    <slot
      v-if="numberOfSlides > 1"
      name="controls"
      v-bind="{ back: () => glider?.go('<'), forward: () => glider?.go('>') }"
    />
    <slot v-if="numberOfSlides > 1" name="bullets" />
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, provide, nextTick } from 'vue';
  import Glide from '@glidejs/glide';
  import '@glidejs/glide/dist/css/glide.core.min.css';

  import { freeCarouselConfig } from '../config';

  const props = defineProps({
    carouselConfig: {
      type: Object as () => Partial<Glide.Options>,
      default: () => ({
        rewind: false,
        peek: 20,
      }),
    },
  });

  const emit = defineEmits<{
    onActiveSlideChange: [index: number, direction: '<' | '>'];
  }>();

  const carouselRef = ref();
  const trackRef = ref();
  const numberOfSlides = ref(0);
  const glider = ref<Glide | null>(null);

  const emitActiveSlideIndex = (move?: { direction: '<' | '>' }) => {
    if (glider.value && move) {
      emit('onActiveSlideChange', glider.value.index, move.direction);
    }
  };

  defineExpose({ glider });

  provide(freeCarouselConfig.gliderInjectionKey, glider);
  provide(freeCarouselConfig.numberOfSlidesInjectionKey, numberOfSlides);

  onMounted(() => {
    numberOfSlides.value = trackRef.value.children.length;
    nextTick(() => {
      glider.value = new Glide(carouselRef.value, props.carouselConfig)
        .on('run', emitActiveSlideIndex)
        .mount(); // Need to wait next tick for bullets to render before Glide initialization
    });
  });
</script>
